import React, { useState, useEffect } from 'react';
import { Box, Heading, Progress, useDisclosure, Button, Text, useMediaQuery, HStack, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, VStack, Icon } from '@chakra-ui/react';
import { WarningTwoIcon } from '@chakra-ui/icons';
import { FaCopy } from 'react-icons/fa';

import FiltrosBusca from '../components/perguntas/FiltrosBusca';
import ListaPerguntas from '../components/perguntas/ListaPerguntas';
import ModalRespostasProntas from '../components/perguntas/ModalRespostasProntas';
import Paginacao from '../components/perguntas/Paginacao';
import ScrollToTop from '../components/ScrollToTop';
import Preloader from '../components/Preloader';
import MetricasPerguntas from '../components/perguntas/MetricasPerguntas';

import { usePerguntas } from '../hooks/usePerguntas';
import { RESPOSTAS_PRONTAS } from '../utils/constants';
import { getMetricasPerguntas, excluirPerguntaML } from '../services/api';

const Perguntas = () => {
    const {
        perguntas,
        loading,
        filtros,
        setFiltros,
        fetchPerguntas,
        responderPergunta,
        pagination,
        submittingId,
        answeredIds
    } = usePerguntas();

    const [metricas, setMetricas] = useState(null);
    const [loadingMetricas, setLoadingMetricas] = useState(true);

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [inserirTextoCallback, setInserirTextoCallback] = useState(null);

    const toast = useToast();
    const [deletingId, setDeletingId] = useState(null);

    const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
    const [perguntaToExcluir, setPerguntaToExcluir] = useState(null);

    const handlePrepararExclusao = (perguntaId, contaId) => {
        setPerguntaToExcluir({ id: perguntaId, conta: contaId });
        onDeleteOpen();
    };

    const handleExcluirPergunta = async () => {
        if (!perguntaToExcluir) return;

        const { id: perguntaId, conta: contaId } = perguntaToExcluir;
        setDeletingId(perguntaId);

        try {
            const res = await excluirPerguntaML(perguntaId, contaId);
            if (res && res.status === 200) {
                toast({
                    title: 'Pergunta excluída!',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                fetchPerguntas(pagination.currentPage);
            } else {
                toast({
                    title: 'Erro ao excluir',
                    description: 'Não foi possível excluir a pergunta.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Erro de conexão',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setDeletingId(null);
            onDeleteClose();
            setPerguntaToExcluir(null);
        }
    };

    // Estados para controlar a animação do botão flutuante
    const [isHovering, setIsHovering] = useState(false);
    const [isMobile] = useMediaQuery("(max-width: 768px)");

    useEffect(() => {
        const buscarMetricas = async () => {
            setLoadingMetricas(true);
            const data = await getMetricasPerguntas();
            setMetricas(data);
            setLoadingMetricas(false);
        };
        buscarMetricas();
    }, []);

    const handleFiltroChange = (novosFiltros) => {
        setFiltros(prev => ({ ...prev, ...novosFiltros }));
    };

    const handleAbrirRespostasComInsercao = (callback) => {
        setInserirTextoCallback(() => callback);
        onOpen();
    };

    const handleAbrirRespostasGlobal = () => {
        setInserirTextoCallback(null);
        onOpen();
    };

    const handleSelecionarResposta = (texto) => {
        if (inserirTextoCallback) {
            inserirTextoCallback(texto);
        }
        onClose();
    };

    const handlePageChange = (page) => {
        fetchPerguntas(page);
    };

    return (
        <>
            <ScrollToTop />
            {loading && (!perguntas || perguntas.length === 0) && <Preloader />}

            <Box minH="100vh" p={{ base: 2, md: 6, lg: 8 }} pb="100px">
                <Box mx="auto" maxW="1200px" boxShadow='md' borderRadius="lg" bg="white" p={{ base: 3, md: 6 }}>
                    <Heading color="orange.500" mb={6} fontSize={{ base: "xl", md: "2xl" }}>
                        PERGUNTAS DO MERCADO LIVRE
                    </Heading>

                    <MetricasPerguntas isLoading={loadingMetricas} metricas={metricas} />

                    <FiltrosBusca
                        filtros={filtros}
                        onFiltroChange={handleFiltroChange}
                        onBuscar={() => fetchPerguntas(1)}
                        isLoading={loading}
                    />

                    <ListaPerguntas
                        perguntas={perguntas}
                        isLoading={loading}
                        onResponder={responderPergunta}
                        onAbrirRespostasProntas={handleAbrirRespostasComInsercao}
                        submittingId={submittingId}
                        answeredIds={answeredIds}
                        onExcluir={handlePrepararExclusao}
                        deletingId={deletingId}
                    />

                    <Paginacao
                        currentPage={pagination.currentPage}
                        lastPage={pagination.lastPage}
                        total={pagination.total}
                        onPageChange={handlePageChange}
                    />
                </Box>
                {loading && <Progress size='xs' isIndeterminate colorScheme="orange" mt={4} />}
            </Box>
            <Button
                position="fixed"
                top={{ base: "70px", md: "100px" }}
                right={{ base: "20px", md: "40px" }}
                colorScheme="teal"
                borderRadius="full"
                boxShadow="lg"
                onClick={handleAbrirRespostasGlobal}
                h={{ base: "50px", md: "60px" }}
                w={!isMobile && isHovering ? "auto" : { base: "50px", md: "60px" }}
                px={!isMobile && isHovering ? 4 : 0}
                zIndex="sticky"
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                transition="all 0.3s ease-in-out"
            >
                <HStack spacing={2}>
                    <FaCopy size="1.5em" />
                    {!isMobile && isHovering && (
                        <Text as="span" whiteSpace="nowrap" fontWeight="semibold">
                            Respostas Prontas
                        </Text>
                    )}
                </HStack>
            </Button>

            <ModalRespostasProntas
                isOpen={isOpen}
                onClose={onClose}
                respostas={RESPOSTAS_PRONTAS}
                onSelecionarResposta={inserirTextoCallback ? handleSelecionarResposta : null}
            />

            <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader color="red.600">
                        <HStack>
                            <Icon as={WarningTwoIcon} />
                            <Text>Confirmar Exclusão</Text>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text>
                            Tem certeza de que deseja excluir esta pergunta do Mercado Livre?
                            <br /><br />
                            <b>Atenção:</b> Esta ação é permanente e a pergunta será removida tanto da sua conta quanto do painel do cliente.
                        </Text>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onDeleteClose} isDisabled={deletingId !== null}>
                            Cancelar
                        </Button>
                        <Button
                            colorScheme="red"
                            onClick={handleExcluirPergunta}
                            isLoading={deletingId !== null}
                            loadingText="Excluindo..."
                        >
                            Sim, Excluir
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </>
    );
};

export default Perguntas;
